import {Box, Stack, Typography} from '@mui/material';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ReactComponent as InfoIcon} from './../../../../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import dayjs, {ConfigType} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './GenerateArtifacts.scss';
import ArrTooltip from '../../../../../common/arr-tooltip/ArrTooltip';
import {useState} from 'react';
import GenerateArtifactsModal from './generateArtifactsModal/GenerateArtifactsModal';
import {useReviewHeaderContext} from '../../../header/ReviewHeader';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ArrToggle} from '../../../../../common/arr-toggle/ArrToggle';
import {
  useGenerateProtocolArtifacts,
  useUpdateProtocolActiveState,
} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import {
  ProtocolIntelligenceStatusTypes,
  ProtocolVersion,
  UpdateProtocolActiveStatePayload,
} from '../../../../../../api/types/aiReview.type';
import {KeyboardArrowRight} from '@mui/icons-material';

dayjs.extend(utc);

export const formatUtcTimestamp = (date?: ConfigType) =>
  dayjs(date).utc().format('YYYY/MM/DD HH:mm:ss [UTC]');

const GenerateArtifacts = ({protocol}: {protocol: ProtocolVersion}) => {
  const {
    handleShowReviewAndApproveOpen,
    setIsEditingProtocol,
    setReviewHeaderToast,
    clearSearchAndFetchProtocolList,
    setSelectedProtocolId,
  } = useReviewHeaderContext();

  const {mutate: updateProtocolActiveState} = useUpdateProtocolActiveState();
  const {mutate: generateProtocolArtifacts} = useGenerateProtocolArtifacts();

  const [showGenerateArtifactsModal, setShowGenerateArtifactsModal] =
    useState<boolean>(false);

  const handleGenerateArtifactsOpen = () => {
    setShowGenerateArtifactsModal(true);
  };

  const handleArtifactsGenerateModalClose = () => {
    setShowGenerateArtifactsModal(false);
  };

  const handleGenerateArtifacts = () => {
    const {protocolId} = protocol;
    const payload = {protocolId};
    generateProtocolArtifacts(payload, {
      onSuccess: () => {
        setReviewHeaderToast({
          isOpen: true,
          message: t(
            'ai.review.protocolIntelligence.artifactsGenerationStartToastSuccess'
          ),
          status: ToastType.Success,
        });
        clearSearchAndFetchProtocolList();
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t(
              'ai.review.protocolIntelligence.artifactsGenerationStartToastFailure'
            ),
          status: ToastType.Error,
        });
      },
    });
  };

  const handleReviewArtifacts = () => {
    setSelectedProtocolId(protocol.protocolId);
    handleShowReviewAndApproveOpen();
  };

  //keeping this, can be removed at the time of integration
  const handleActiveChange = () => {
    if (!protocol.protocolId) return;
    const payload: UpdateProtocolActiveStatePayload = {
      protocolId: protocol.protocolId,
      isActive: Number(!protocol.isActive) as 0 | 1, // Convert boolean to 0 or 1
    };
    updateProtocolActiveState(payload, {
      onSuccess: () => {
        setReviewHeaderToast({
          isOpen: true,
          message: t(
            'ai.review.protocolIntelligence.isProtocolActiveUpdateSuccess'
          ),
          status: ToastType.Success,
        });
        clearSearchAndFetchProtocolList();
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t('ai.review.protocolIntelligence.isProtocolActiveUpdateFailure'),
          status: ToastType.Error,
        });
      },
    });
  };

  // ==================CORE changes on Functional flow validators for the implementaiton of Edit Functionality========================
  // Functional flow validators
  const prtotocolAIStatus = protocol?.status?.toLowerCase();

  // Step1 Uploaded = Uploaded + AiInitiated + RegisteredFailed + PendingFailed + CompletedFailed =================
  const generatingArtifactsFailedStatuses = [
    ProtocolIntelligenceStatusTypes.AiInitiated,
    ProtocolIntelligenceStatusTypes.RegisteredFailed,
    ProtocolIntelligenceStatusTypes.PendingFailed,
    ProtocolIntelligenceStatusTypes.CompletedFailed,
  ].map((status) => status.toLowerCase());

  const protocolUploadedAndArtifactsNotGenerated =
    prtotocolAIStatus ===
      ProtocolIntelligenceStatusTypes.Uploaded.toLowerCase() ||
    generatingArtifactsFailedStatuses.includes(prtotocolAIStatus);

  // Step2  Pending status = Pending + Registered  =================
  const pendingStates = [
    ProtocolIntelligenceStatusTypes.Pending,
    ProtocolIntelligenceStatusTypes.Registered,
  ].map((status) => status.toLowerCase());
  const protocolGenerationPending = pendingStates.includes(prtotocolAIStatus);

  // Step3 Completed status = Completed + DraftFailed + FinalFail - isApproved=================
  const completedStates = [
    ProtocolIntelligenceStatusTypes.Completed,
    ProtocolIntelligenceStatusTypes.DraftFailed,
    ProtocolIntelligenceStatusTypes.FinalFail,
  ].map((status) => status.toLowerCase());
  const protocolGeneratedButNotApproved =
    completedStates.includes(prtotocolAIStatus) && !protocol?.isApproved;

  // Step4 Draft status = Draft - isApproved =================
  const isdraftPendingStatus =
    prtotocolAIStatus === ProtocolIntelligenceStatusTypes.Draft.toLowerCase() &&
    !protocol?.isApproved;

  // Step5 Final status = Final + isApproved=================
  const protocolGeneratedAndApproved =
    prtotocolAIStatus === ProtocolIntelligenceStatusTypes.Final.toLowerCase() &&
    protocol?.isApproved;

  const handleFileNameClick = () => {
    if (protocolGeneratedAndApproved) {
      setSelectedProtocolId(protocol.protocolId);
      setIsEditingProtocol(true);
    }
  };

  return (
    <>
      <Stack className="file-information" spacing={2}>
        <Stack
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'flex-start'}
          className="file-information__header"
        >
          <Stack gap={1} className="file-information__details">
            <ArrTooltip
              data-testid={`tooltip-uploaded-file-protocolIntelligence`}
              content={protocol?.protocolName || ''}
              labelId={`tooltip-uploaded-file-${protocol?.protocolName}`}
            >
              <Typography
                className={`typo-text subhead4_size ${protocolGeneratedAndApproved ? 'upload-approved' : 'uploaded-file-name'}`}
                data-testid={`file-name-text-approve-${protocol?.protocolName}`}
                onClick={handleFileNameClick}
                aria-label={protocol?.protocolName || 'no-file-name'}
              >
                {protocol?.protocolName || 'no-file-name'}
              </Typography>
            </ArrTooltip>
            <Box
              className="upload-moment-description"
              aria-label={`${
                protocol.isUploaded
                  ? t('ai.review.protocolIntelligence.uploaded')
                  : t('ai.review.protocolIntelligence.created')
              } ${formatUtcTimestamp(protocol?.lastUpdatedAt)}`}
              tabIndex={0}
            >
              <Box>
                {protocol.isUploaded
                  ? t('ai.review.protocolIntelligence.uploaded')
                  : t('ai.review.protocolIntelligence.created')}
              </Box>
              {formatUtcTimestamp(protocol?.lastUpdatedAt)}
              {` `}
              {t('ai.review.protocolIntelligence.by')}
              {` `}
              {protocol?.createdBy}
            </Box>
          </Stack>
          <Box className="file-information__actions" alignContent={'center'}>
            {protocolUploadedAndArtifactsNotGenerated && (
              <ArrButton
                size="small"
                aria-label={t(
                  'ai.review.protocolIntelligence.generateArtifacts'
                )}
                tabIndex={0}
                onClick={handleGenerateArtifactsOpen}
                endIcon={
                  <InfoIcon
                    className="generate-artifacts-info-icon"
                    fontSize="medium"
                    aria-label="information"
                    data-testid="generate-artifacts-info-icon"
                    tabIndex={0}
                  />
                }
                dataTestId="generate-artifacts-generate-artifacts-button"
              >
                {t('ai.review.protocolIntelligence.generateArtifacts')}
              </ArrButton>
            )}
            {(protocolGenerationPending || isdraftPendingStatus) && (
              <Stack direction={'row'} alignItems={'flex-end'}>
                <Box>
                  <ArrLoadingSpinner size={18} />
                </Box>
                {protocolGenerationPending && (
                  <Box className="generatingArtifacts text-headings warning-message-text mx-10">
                    {t('ai.review.protocolIntelligence.generatingArtifacts')}
                  </Box>
                )}
                {isdraftPendingStatus && (
                  <Box className="generatingArtifacts text-headings warning-message-text mx-10">
                    {t('ai.review.protocolIntelligence.draftPending')}
                  </Box>
                )}
              </Stack>
            )}
            {(protocolGeneratedButNotApproved ||
              protocolGeneratedAndApproved) && (
              <Stack direction={'row'}>
                <Box className="mr-10">
                  <Typography
                    className={
                      protocolGeneratedButNotApproved
                        ? 'text-inactive'
                        : 'text-subtle'
                    }
                    data-testid={`generate-artifacts-active`}
                  >
                    {t('ai.review.protocolIntelligence.active')}

                    <ArrTooltip
                      content={t(
                        'ai.review.protocolIntelligence.activeTooltip'
                      )}
                      labelId={`tooltip-intelligence-active`}
                    >
                      <InfoIcon
                        className={
                          'isReviewedAndApproved-info-icon ml-4 pos-2' +
                          (protocol?.isApproved ? ' isApproved-info-icon' : '')
                        }
                        fontSize="small"
                        aria-label={t(
                          'ai.review.protocolIntelligence.activeTooltip'
                        )}
                        data-testid="generate-artifacts-isApprovalPending-isReviewedAndApproved-info-icon"
                      />
                    </ArrTooltip>
                  </Typography>
                </Box>
                <ArrToggle
                  toggleSize="large"
                  disabled={protocolGeneratedButNotApproved}
                  checked={Boolean(protocol?.isActive) ?? false}
                  onChange={handleActiveChange}
                  className="mi-active-toggle"
                  dataTestId="protocol-intelligence-toggle"
                />
              </Stack>
            )}
          </Box>
        </Stack>
        {protocolGenerationPending && (
          <Box className="warning-banner text-headings surface-info-bg">
            <InfoIcon
              className="isFileSubmittedAndArtifactsNotGeneratedMessage-info-icon"
              aria-label={t(
                'ai.review.protocolIntelligence.isFileSubmittedAndArtifactsNotGeneratedMessage'
              )}
              data-testid="generate-artifacts-isFileSubmittedAndArtifactsNotGeneratedMessage-info-icon"
              tabIndex={0}
            />

            <Typography
              className="typo-text body3_size text-headings pl-16"
              data-testid={`generate-artifacts-is-file-submitted-and-artifacts-not-generated-message`}
            >
              {t(
                'ai.review.protocolIntelligence.isFileSubmittedAndArtifactsNotGeneratedMessage'
              )}
            </Typography>
          </Box>
        )}
        {protocolGeneratedButNotApproved && (
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            className="surface-warning-bg warning-banner"
          >
            <Box className="text-warning">
              <InfoIcon
                className="mr-10"
                aria-label={t(
                  'ai.review.protocolIntelligence.isApprovalPending'
                )}
                data-testid="generate-artifacts-isApprovalPending-info-icon"
                tabIndex={0}
              />
              <Typography
                aria-label={t(
                  'ai.review.protocolIntelligence.isApprovalPending'
                )}
                tabIndex={0}
                className="typo-text body3_size"
                data-testid={`generate-artifacts-is-approval-pending`}
              >
                {t('ai.review.protocolIntelligence.isApprovalPending')}
              </Typography>
            </Box>
            <ArrButton
              size="small"
              aria-label={t('ai.review.protocolIntelligence.reviewArtifacts')}
              tabIndex={0}
              onClick={handleReviewArtifacts}
              endIcon={<KeyboardArrowRight />}
              dataTestId="review-artifact-button"
            >
              {t('ai.review.protocolIntelligence.reviewArtifacts')}
            </ArrButton>
          </Stack>
        )}
      </Stack>
      <GenerateArtifactsModal
        open={showGenerateArtifactsModal}
        onClose={handleArtifactsGenerateModalClose}
        handleGenerateArtifacts={handleGenerateArtifacts}
      />
    </>
  );
};

export default GenerateArtifacts;
